// src/components/ContactUs.js
import React, { useState } from "react";
import {
  Container,
  Box,
  Typography,
  Paper,
  Button,
  TextField,
  Grid,
  Alert,
  CircularProgress,
} from "@mui/material";
import {
  ArrowBack,
  Email,
  Phone,
  LocationOn,
  AccessTime,
  Send,
} from "@mui/icons-material";
import { MERCHANT_LEGAL } from "../config/merchantLegalConfig";
import MerchantLegalNotice from "./MerchantLegalNotice";

const ContactUs = ({ onBack, primaryBlue, glassStyles }) => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    subject: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState("");
  const [error, setError] = useState("");

  const defaultPrimaryBlue = primaryBlue || {
    50: "#F0F8FF",
    100: "#E6F0FA",
    500: "#1E40AF",
    600: "#002366",
    700: "#001A4D",
    800: "#0B1C48",
  };

  const defaultGlassStyles = glassStyles || {
    background: "rgba(255, 255, 255, 0.25)",
    backdropFilter: "blur(20px)",
    border: "1px solid rgba(255, 255, 255, 0.18)",
    borderRadius: "16px",
    boxShadow: "0 8px 32px rgba(11, 28, 72, 0.37)",
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    setSuccess("");

    // Basic validation
    if (!formData.name || !formData.email || !formData.message) {
      setError("Please fill in all required fields");
      setLoading(false);
      return;
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setError("Please enter a valid email address");
      setLoading(false);
      return;
    }

    try {
      // Simulate form submission (replace with actual API call)
      await new Promise((resolve) => setTimeout(resolve, 1500));

      setSuccess(
        "Thank you for contacting us! We will get back to you within 24 hours."
      );
      setFormData({
        name: "",
        email: "",
        phone: "",
        subject: "",
        message: "",
      });
    } catch (err) {
      setError("Failed to send message. Please try again or contact us directly.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background: `linear-gradient(135deg, ${defaultPrimaryBlue[50]} 0%, ${defaultPrimaryBlue[100]} 100%)`,
        py: 4,
      }}
    >
      <Container maxWidth="lg">
        <Button
          startIcon={<ArrowBack />}
          onClick={onBack}
          sx={{
            mb: 3,
            color: defaultPrimaryBlue[700],
            fontWeight: 600,
          }}
        >
          Back
        </Button>

        <Paper
          elevation={0}
          sx={{
            ...defaultGlassStyles,
            p: { xs: 3, md: 5 },
            bgcolor: "rgba(255, 255, 255, 0.9)",
          }}
        >
          <Typography
            variant="h3"
            gutterBottom
            sx={{
              fontWeight: 700,
              color: defaultPrimaryBlue[700],
              mb: 2,
              textAlign: "center",
            }}
          >
            Contact Us
          </Typography>

          <Typography
            variant="body1"
            paragraph
            sx={{ textAlign: "center", mb: 4, color: "text.secondary" }}
          >
            Have questions? We'd love to hear from you. Send us a message and
            we'll respond as soon as possible.
          </Typography>

          <Grid container spacing={4}>
            {/* Contact Information */}
            <Grid item xs={12} md={5}>
              <Box sx={{ mb: 4 }}>
                <Typography
                  variant="h5"
                  gutterBottom
                  sx={{ fontWeight: 600, color: defaultPrimaryBlue[700], mb: 3 }}
                >
                  Get in Touch
                </Typography>

                {/* Email */}
                <Box sx={{ display: "flex", alignItems: "flex-start", mb: 3 }}>
                  <Email
                    sx={{
                      color: defaultPrimaryBlue[600],
                      mr: 2,
                      mt: 0.5,
                      fontSize: 28,
                    }}
                  />
                  <Box>
                    <Typography
                      variant="subtitle1"
                      sx={{ fontWeight: 600, mb: 0.5 }}
                    >
                      Email
                    </Typography>
                    <Typography
                      variant="body1"
                      component="a"
                      href={`mailto:${MERCHANT_LEGAL.supportEmail}`}
                      sx={{
                        color: defaultPrimaryBlue[600],
                        textDecoration: "none",
                        "&:hover": { textDecoration: "underline" },
                      }}
                    >
                      {MERCHANT_LEGAL.supportEmail}
                    </Typography>
                  </Box>
                </Box>

                {/* Phone */}
                <Box sx={{ display: "flex", alignItems: "flex-start", mb: 3 }}>
                  <Phone
                    sx={{
                      color: defaultPrimaryBlue[600],
                      mr: 2,
                      mt: 0.5,
                      fontSize: 28,
                    }}
                  />
                  <Box>
                    <Typography
                      variant="subtitle1"
                      sx={{ fontWeight: 600, mb: 0.5 }}
                    >
                      Phone
                    </Typography>
                    <Typography
                      variant="body1"
                      component="a"
                      href={`tel:${MERCHANT_LEGAL.supportPhoneTel}`}
                      sx={{
                        color: defaultPrimaryBlue[600],
                        textDecoration: "none",
                        "&:hover": { textDecoration: "underline" },
                      }}
                    >
                      {MERCHANT_LEGAL.supportPhone}
                    </Typography>
                  </Box>
                </Box>

                {/* Business Hours */}
                <Box sx={{ display: "flex", alignItems: "flex-start", mb: 3 }}>
                  <AccessTime
                    sx={{
                      color: defaultPrimaryBlue[600],
                      mr: 2,
                      mt: 0.5,
                      fontSize: 28,
                    }}
                  />
                  <Box>
                    <Typography
                      variant="subtitle1"
                      sx={{ fontWeight: 600, mb: 0.5 }}
                    >
                      Business Hours
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                      Monday - Saturday
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                      9:00 AM - 6:00 PM IST
                    </Typography>
                  </Box>
                </Box>

                {/* Address */}
                <Box sx={{ display: "flex", alignItems: "flex-start", mb: 3 }}>
                  <LocationOn
                    sx={{
                      color: defaultPrimaryBlue[600],
                      mr: 2,
                      mt: 0.5,
                      fontSize: 28,
                    }}
                  />
                  <Box>
                    <Typography
                      variant="subtitle1"
                      sx={{ fontWeight: 600, mb: 0.5 }}
                    >
                      Address
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                      {MERCHANT_LEGAL.brandName}
                      <br />
                      {MERCHANT_LEGAL.addressLines.map((line) => (
                        <React.Fragment key={line}>
                          {line}
                          <br />
                        </React.Fragment>
                      ))}
                    </Typography>
                  </Box>
                </Box>

                {/* GST */}
                <Box
                  sx={{
                    mt: 4,
                    p: 2,
                    bgcolor: defaultPrimaryBlue[50],
                    borderRadius: 2,
                  }}
                >
                  <Typography
                    variant="subtitle2"
                    sx={{ fontWeight: 600, mb: 0.5 }}
                  >
                    GST Number
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {MERCHANT_LEGAL.gstin}
                  </Typography>
                </Box>

                <Box
                  sx={{
                    mt: 3,
                    p: 2,
                    bgcolor: defaultPrimaryBlue[50],
                    borderRadius: 2,
                    border: `1px solid ${defaultPrimaryBlue[200]}`,
                  }}
                >
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5 }}>
                    Contact information
                  </Typography>
                  <MerchantLegalNotice variant="body2" />
                </Box>
              </Box>
            </Grid>

            {/* Contact Form */}
            <Grid item xs={12} md={7}>
              <Box
                component="form"
                onSubmit={handleSubmit}
                sx={{
                  bgcolor: "rgba(255, 255, 255, 0.5)",
                  p: { xs: 3, md: 4 },
                  borderRadius: 3,
                  border: `1px solid ${defaultPrimaryBlue[200]}`,
                }}
              >
                <Typography
                  variant="h5"
                  gutterBottom
                  sx={{ fontWeight: 600, color: defaultPrimaryBlue[700], mb: 3 }}
                >
                  Send us a Message
                </Typography>

                {success && (
                  <Alert severity="success" sx={{ mb: 3 }}>
                    {success}
                  </Alert>
                )}

                {error && (
                  <Alert severity="error" sx={{ mb: 3 }}>
                    {error}
                  </Alert>
                )}

                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      required
                      label="Your Name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      disabled={loading}
                      sx={{
                        bgcolor: "white",
                        borderRadius: 1,
                      }}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      required
                      label="Email Address"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      disabled={loading}
                      sx={{
                        bgcolor: "white",
                        borderRadius: 1,
                      }}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Phone Number"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      disabled={loading}
                      sx={{
                        bgcolor: "white",
                        borderRadius: 1,
                      }}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Subject"
                      name="subject"
                      value={formData.subject}
                      onChange={handleChange}
                      disabled={loading}
                      sx={{
                        bgcolor: "white",
                        borderRadius: 1,
                      }}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      required
                      label="Message"
                      name="message"
                      multiline
                      rows={6}
                      value={formData.message}
                      onChange={handleChange}
                      disabled={loading}
                      placeholder="Tell us how we can help you..."
                      sx={{
                        bgcolor: "white",
                        borderRadius: 1,
                      }}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <Button
                      type="submit"
                      variant="contained"
                      size="large"
                      fullWidth
                      disabled={loading}
                      startIcon={
                        loading ? <CircularProgress size={20} /> : <Send />
                      }
                      sx={{
                        py: 1.5,
                        background: `linear-gradient(45deg, ${defaultPrimaryBlue[600]}, ${defaultPrimaryBlue[700]})`,
                        fontWeight: 600,
                        fontSize: "1.1rem",
                        "&:hover": {
                          background: `linear-gradient(45deg, ${defaultPrimaryBlue[700]}, ${defaultPrimaryBlue[800]})`,
                        },
                      }}
                    >
                      {loading ? "Sending..." : "Send Message"}
                    </Button>
                  </Grid>
                </Grid>
              </Box>
            </Grid>
          </Grid>

          {/* Additional Information */}
          <Box sx={{ mt: 5, pt: 4, borderTop: `1px solid ${defaultPrimaryBlue[200]}` }}>
            <Typography
              variant="h5"
              gutterBottom
              sx={{ fontWeight: 600, color: defaultPrimaryBlue[700], mb: 3 }}
            >
              Frequently Asked Questions
            </Typography>

            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                    How long does delivery take?
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Standard delivery takes 7-10 business days from order
                    confirmation. You'll receive tracking information once your
                    order is shipped.
                  </Typography>
                </Box>
              </Grid>

              <Grid item xs={12} md={6}>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                    What payment methods do you accept?
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    We accept UPI, Credit/Debit Cards, Net Banking, and Digital
                    Wallets. All payments are processed securely.
                  </Typography>
                </Box>
              </Grid>

              <Grid item xs={12} md={6}>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                    Can I return or exchange products?
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Yes, we offer a 7-day return policy for eligible products.
                    Please refer to our Return & Refund Policy for details.
                  </Typography>
                </Box>
              </Grid>

              <Grid item xs={12} md={6}>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                    How does the referral program work?
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Share your referral code with friends. When they make a
                    purchase, you earn rewards. Ultra Customers get additional
                    benefits!
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};

export default ContactUs;
